import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

export interface MeProfile {
  id: string;
  username: string;
  role: string;
  is_active: boolean;
  has_2fa: boolean;
  must_change_password: boolean;
  telegram_chat_id: string | null;
  global_roles: string[];
  segments: string[];
  area_roles: Array<{ area_id: string; area_code: string; area_name: string; role: string }>;
  created_at: string;
  last_login_at: string | null;
}

export interface SessionRead {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  expires_at: string;
  is_active: boolean;
}

export interface SessionList {
  items: SessionRead[];
  total: number;
}

export function useMyProfile() {
  return useQuery({
    queryKey: ["me", "profile"],
    queryFn: ({ signal }) => api<MeProfile>("/api/v1/me/profile", { signal }),
    staleTime: 30_000,
  });
}

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { telegram_chat_id?: string | null }) =>
      api<MeProfile>("/api/v1/me/profile", { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "profile"] }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { old_password: string; new_password: string }) =>
      api<void>("/api/v1/me/password", { method: "POST", body }),
  });
}

export function useMySessions() {
  return useQuery({
    queryKey: ["me", "sessions"],
    queryFn: ({ signal }) => api<SessionList>("/api/v1/me/sessions", { signal }),
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api<void>(`/api/v1/me/sessions/${sessionId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "sessions"] }),
  });
}
